import { useEffect, useRef, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, Trash2 } from "lucide-react";

export const BackgroundsAdmin = () => {
  const [list, setList] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [artBusy, setArtBusy] = useState(null);
  const fileRef = useRef(null);
  const load = () => gameApi.get("/game/admin/backgrounds").then((r) => { setList(r.data.backgrounds); setAchievements(r.data.achievements || []); }).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose an image first."); return; }
    if (!name.trim()) { toast.error("Give the background a name."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("name", name); fd.append("price", price || "0");
      await gameApi.post("/game/admin/backgrounds/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Background uploaded");
      setName(""); setPrice("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed. Please try again."); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const save = (id, body) => gameApi.post(`/game/admin/backgrounds/${id}`, body).then((r) => { const g = r.data?.granted || 0; toast.success(g > 0 ? `Saved — granted to ${g} player${g === 1 ? "" : "s"} who already earned it` : "Saved"); load(); }).catch(() => toast.error("Save failed"));
  const remove = (id) => gameApi.delete(`/game/admin/backgrounds/${id}`).then(() => { toast.success("Removed from sale"); load(); }).catch(() => toast.error("Failed"));
  const uploadArt = async (id, file) => {
    if (!file) return;
    setArtBusy(id);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await gameApi.post(`/game/admin/backgrounds/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); const g = r.data?.granted || 0; toast.success(g > 0 ? `Artwork uploaded — granted to ${g} player${g === 1 ? "" : "s"} who already earned it` : "Artwork uploaded"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed. Please try again."); }
    finally { setArtBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-backgrounds">
      <div className="rounded-2xl border border-border bg-card p-4">
        <h3 className="mb-2 font-display text-lg font-bold text-primary">Upload Background</h3>
        <p className="mb-2 text-[11px] text-muted-foreground">PNG / JPEG / WebP, up to 8MB. Shown as a full-bleed layer behind the horse — the horse art is never modified.</p>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" data-testid="bg-file" className="mb-2 w-full text-xs" />
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="bg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Northern Lights)" className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
          <input data-testid="bg-price" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Coin price" className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
        </div>
        <button data-testid="bg-upload" disabled={busy} onClick={upload} className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-primary text-xs font-bold text-primary-foreground disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload Background
        </button>
      </div>

      {!list ? <p className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="inline animate-spin" size={15} /> Loading…</p>
        : list.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No backgrounds yet.</p>
        : list.map((b) => (
          <div key={b.id} data-testid={`bg-row-${b.id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
            {b.image_path ? <img src={b.image_path} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
              : <div className="grid h-14 w-20 shrink-0 place-items-center rounded-lg border border-dashed border-border text-[9px] font-bold text-muted-foreground">No art</div>}
            <div className="min-w-0 flex-1">
              <input data-testid={`bg-edit-name-${b.id}`} defaultValue={b.name} onBlur={(e) => e.target.value !== b.name && save(b.id, { name: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1 text-sm font-bold text-primary" />
              <div className="mt-1.5 flex items-center gap-2">
                <span className="text-[11px] font-semibold text-secondary">🏆 Achievement:</span>
                <select data-testid={`bg-ach-${b.id}`} value={b.unlock || ""} onChange={(e) => save(b.id, { unlock: e.target.value })}
                  className="flex-1 rounded-lg border border-border bg-background px-2 py-1 text-xs font-semibold text-primary">
                  <option value="">No achievement</option>
                  {achievements.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              {b.unlock && <p className="mt-0.5 text-[10px] text-muted-foreground">Auto-granted (one copy) when a player earns this achievement.</p>}
              <div className="mt-1.5 flex items-center gap-2">
                <label className="flex items-center gap-1 text-[11px] font-bold text-muted-foreground">
                  <input type="checkbox" data-testid={`bg-active-${b.id}`} checked={!!b.active} onChange={(e) => save(b.id, { active: e.target.checked })} /> Available in shop
                </label>
                <input data-testid={`bg-edit-price-${b.id}`} defaultValue={b.price} onBlur={(e) => Number(e.target.value) !== b.price && save(b.id, { price: Number(e.target.value || 0) })} className="w-20 rounded-lg border border-border bg-background px-2 py-1 text-xs" placeholder="Price" />
                <span className="text-[11px] text-muted-foreground">coins</span>
              </div>
              {b.active && !b.image_path && <p className="mt-0.5 text-[10px] text-amber-600">Upload artwork below so it shows in the shop.</p>}
              <label className="mt-1.5 block text-[11px] font-semibold text-secondary">
                {artBusy === b.id ? <span className="flex items-center gap-1 text-muted-foreground"><Loader2 className="animate-spin" size={12} /> Uploading…</span> : (b.image_path ? "Replace artwork" : "Upload artwork")}
                <input type="file" accept="image/png,image/jpeg,image/webp" data-testid={`bg-art-${b.id}`} disabled={artBusy === b.id}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadArt(b.id, f); }} className="mt-0.5 block w-full text-[11px] disabled:opacity-50" />
              </label>
            </div>
            <button data-testid={`bg-delete-${b.id}`} onClick={() => remove(b.id)} className="shrink-0 rounded-full p-2 text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
          </div>
        ))}
    </div>
  );
};
